import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Minus, Trash2, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import api from "../lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, deliveryFee, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, discount, total}
  const [promoLoading, setPromoLoading] = useState(false);
  const [consent, setConsent] = useState(() => {
    try { return localStorage.getItem("sandform_research_consent") === "true"; } catch { return false; }
  });

  const toggleConsent = (v) => {
    setConsent(v);
    try { localStorage.setItem("sandform_research_consent", v ? "true" : "false"); } catch { /* ignore */ }
  };

  const addressComplete = user && user.phone && user.address?.line && user.address?.emirate;
  const discount = promo?.discount || 0;
  const grandTotal = promo ? promo.total : total;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", {
        promo_code: promoInput.trim(),
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setPromo({ code: data.code, discount: data.discount, total: data.total });
      toast.success(`Code ${data.code} applied — AED ${data.discount} off`);
    } catch (err) {
      setPromo(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(""); };

  const checkout = async () => {
    if (!user) { navigate("/login", { state: { from: "/cart" } }); return; }
    if (!addressComplete) { toast.error("Complete your delivery details first"); navigate("/account"); return; }
    if (!consent) { toast.error("Please acknowledge the research-use-only terms"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
        research_consent: consent,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] px-5 md:px-10 pt-28 pb-24">
      <div className="max-w-[1100px] mx-auto">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-5xl md:text-7xl uppercase leading-none text-[#F5F5F5] mb-10">
          Checkout
        </motion.h1>

        {items.length === 0 ? (
          <div className="border border-[#1c1c1c] p-12 text-center">
            <p className="text-[#A3A3A3] mb-5">Your cart is empty.</p>
            <button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" }), 300); }} className="bg-[#E6DFD4] text-black px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold">
              Browse Peptides
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-5">
              {items.map((it) => (
                <div key={it.product_id} data-testid={`checkout-item-${it.product_id}`} className="flex gap-5 border border-[#1c1c1c] bg-[#0A0A0A] p-5">
                  <div className="h-24 w-20 shrink-0 bg-[#111] border border-[#1c1c1c] overflow-hidden">
                    <img src={it.vial} alt={it.name} className="h-full w-full object-contain p-1" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-display text-xl tracking-wide uppercase text-[#F5F5F5]">{it.name}</h3>
                      <button onClick={() => removeItem(it.product_id)} className="text-[#737373] hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                    <div className="text-sm text-[#E6DFD4] mt-1">AED {it.price}</div>
                    <div className="mt-4 flex items-center gap-3">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="h-8 w-8 flex items-center justify-center border border-[#2c2c2c] text-[#E6DFD4] hover:bg-[#1a1a1a]"><Minus size={14} /></button>
                      <span className="text-sm text-[#F5F5F5] w-6 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="h-8 w-8 flex items-center justify-center border border-[#2c2c2c] text-[#E6DFD4] hover:bg-[#1a1a1a]"><Plus size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="border border-[#1c1c1c] bg-[#0A0A0A] p-6 sticky top-28">
                <h2 className="font-display text-2xl uppercase text-[#F5F5F5] mb-5">Summary</h2>

                <div className="mb-5">
                  {promo ? (
                    <div className="flex items-center justify-between border border-[#3a382f] bg-[#0f0f0d] px-3 py-2.5">
                      <span className="text-xs tracking-[0.15em] uppercase text-[#E6DFD4]">{promo.code} applied</span>
                      <button data-testid="promo-remove-button" onClick={clearPromo} className="text-xs text-[#737373] hover:text-red-400 uppercase tracking-[0.1em]">Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        data-testid="promo-input"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="Promo code"
                        className="flex-1 bg-[#0D0D0D] border border-[#222] focus:border-[#E6DFD4] outline-none text-[#F5F5F5] px-3 py-2.5 text-sm placeholder:text-[#5a5a5a] uppercase tracking-[0.1em] transition-colors"
                      />
                      <button
                        data-testid="promo-apply-button"
                        onClick={applyPromo}
                        disabled={promoLoading}
                        className="px-4 border border-[#2c2c2c] text-[#E6DFD4] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#111] transition-colors disabled:opacity-50"
                      >
                        {promoLoading ? "..." : "Apply"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3 text-sm text-[#A3A3A3]">
                  <div className="flex justify-between"><span>Subtotal</span><span>AED {subtotal}</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between text-[#E6DFD4]" data-testid="discount-line"><span>Discount ({promo.code})</span><span>− AED {discount}</span></div>
                  )}
                  <div className="flex justify-between"><span>Delivery</span><span>{deliveryFee === 0 ? "Free" : `AED ${deliveryFee}`}</span></div>
                </div>
                <div className="flex justify-between font-display text-3xl text-[#F5F5F5] mt-4 pt-4 border-t border-[#1c1c1c]"><span>Total</span><span data-testid="cart-grand-total">AED {grandTotal}</span></div>

                {user && !addressComplete && (
                  <p className="mt-4 text-xs text-[#E6DFD4]">Complete your delivery details in your account to continue.</p>
                )}

                <label data-testid="research-consent-label" className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                  <input
                    data-testid="research-consent-checkbox"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => toggleConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#E6DFD4] cursor-pointer"
                  />
                  <span className="text-[11px] leading-relaxed text-[#A3A3A3]">
                    I confirm these products are purchased strictly for <span className="text-[#E6DFD4]">laboratory and research purposes only</span> — not for human consumption, personal use, or veterinary use.
                  </span>
                </label>

                <button
                  data-testid="pay-now-button"
                  onClick={checkout}
                  disabled={loading || (user && !consent)}
                  className="w-full mt-5 flex items-center justify-center gap-2 bg-[#E6DFD4] text-black py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? "Redirecting..." : user ? "Pay Securely" : "Login to Continue"}
                  <ArrowRight size={16} />
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-[10px] tracking-[0.15em] uppercase text-[#737373]">
                  <Lock size={12} /> Secured by Stripe · Visa · Apple Pay · Google Pay
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
